import React, { useState, useEffect } from 'react';
import OnlineBookReader from './OnlineBookReader';
import { useReadingProgress } from '../../hooks/useReadingProgress';
import { bookService } from '../../services/bookService';
import { Book } from '../../types/Book';
import './FavouriteBooks.css';

interface FavouriteBooksProps {
  onStartReading?: (book?: Book) => void;
  onStopReading?: () => void;
  selectedBook?: Book | null;
}

const FavouriteBooks: React.FC<FavouriteBooksProps> = ({ onStartReading, onStopReading, selectedBook: propSelectedBook }) => {
  const [favoriteBooks, setFavoriteBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  
  // Reading progress tracking (keeping for future use)
  const { getBookProgress } = useReadingProgress();

  useEffect(() => {
    const loadBooks = async () => {
      try {
        setLoading(true);
        
        // Load only favorite books
        const favoriteBooksData = await bookService.getUserFavorites(50, 0);
        setFavoriteBooks(favoriteBooksData);
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load favorite books');
      } finally {
        setLoading(false);
      }
    };

    loadBooks();
  }, []);

  // Handle when a book is selected from parent (fullscreen mode)
  useEffect(() => {
    if (propSelectedBook) {
      setSelectedBook(propSelectedBook);
    }
  }, [propSelectedBook]);

  const handleBookSelect = (book: Book) => {
    setSelectedBook(book);
    if (onStartReading) {
      onStartReading(book);
    }
  };

  const handleBackToList = () => {
    setSelectedBook(null);
    if (onStopReading) {
      onStopReading();
    }
  };

  if (selectedBook) {
    return (
      <OnlineBookReader 
        book={selectedBook} 
        onClose={handleBackToList} 
      />
    );
  }

  if (loading) {
    return (
      <div className="favourite-books-loading">
        <div className="loading-spinner"></div>
        <p>Loading your favorite books...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="favourite-books-error">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="favourite-books-container">
      <div className="available-books-grid">
        {favoriteBooks.map((book) => {
          // Note: Progress tracking removed since we're not loading local metadata
          // Can be re-implemented with online book progress tracking in the future
          
          return (
            <div 
              key={book.id} 
              className="available-book-card favorite-book"
              onClick={() => handleBookSelect(book)}
            >
              <div className="available-book-info">
                <div className="favorite-indicator">❤️</div>
                <h3 className="available-book-title">{book.title}</h3>
                <p className="available-book-author">by {book.author}</p>
                <p className="available-book-genre">{book.genre}</p>
                
                {book.favoriteNotes && (
                  <div className="favorite-notes">
                    <small>📝 {book.favoriteNotes}</small>
                  </div>
                )}
              </div>
              <div className="available-book-action">
                <span className="read-now-btn">
                  Read Now →
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {favoriteBooks.length === 0 && (
        <div className="no-books-message">
          <p>No favorite books yet. Add some books to your favorites to see them here!</p>
        </div>
      )}
    </div>
  );
};

export default FavouriteBooks;
